import PartnerTemplate from '../components/marketing/PartnerTemplate';

/* ============================================================
   First National Bankers Bank (FNBB) × Clarid — partnership page.

   AUDIENCE: Jay Wisener and the FNBB team, after their meeting
   with Jason Willard and Michael Wallace (2026-10-09). FNBB is a
   bankers' bank — its customers ARE community banks — so, like
   BBOK, this is a channel/correspondent argument.

   TWO THINGS JAY ASKED FOR, via Jason:
     1. Revenue sharing — "they are actively seeking new areas to
        drive revenue." Given its own section. NO percentages or
        terms are stated: none have been agreed, and the page says
        so.
     2. Overlap — FNBB's services and Clarid's overlap, and the
        page must say Clarid won't go after FNBB's clients and
        take revenue from FNBB. Given its own section, with the
        overlaps NAMED from FNBB's own service list rather than
        left vague.

   EVERYTHING ABOUT FNBB COMES FROM FNBB: bankers-bank.com/about
   (chartered July 2, 1984 as the first nationally chartered
   bankers' bank; Baton Rouge; relationships in more than 30
   states; "Our only business is supporting the growth of
   community banks … We are a partner in the truest sense and
   never compete for their business") and the two printed
   service sheets Jason forwarded (FNBB Services Corp., FNBB
   Capital Markets, FNBB Insurance Agency, strategic alliances).
   Alvarez & Marsal and Duane Morris both shipped factual errors
   by inferring from marketing copy; nothing here is inferred.

   ALLIANCE PARTNERS ARE NOT NAMED. FNBB's sheet lists a social
   media compliance alliance, which overlaps Clarid's marketing
   review. The overlap section covers it as "services FNBB
   delivers through its strategic alliances" rather than naming a
   third party on a page about FNBB.

   CRA INVESTMENT TEST: FNBB Capital Markets trades securities
   with its banks. Clarid validates CRA investment credit at the
   bond level. That validation is only worth something if it is
   independent of the party selling the bond, so the page states
   that the institution — not the seller — is the client for it.

   Clarid capability text is reused from the BBOK and ABS pages,
   where it was verified against production (170+ = 171 edits).
   Roadmap is marked as roadmap.
   ============================================================ */

export default function FNBBPartnerPage() {
  return (
    <PartnerTemplate
      docTitle="First National Bankers Bank + Clarid — Where We Can Work Together"
      lockup="dual"
      partnerName="First National Bankers Bank"
      eyebrow="§ PARTNERSHIP · FIRST NATIONAL BANKERS BANK × CLARID"
      headline={
        <>
          A partner in the truest sense —{' '}
          <span className="frame-quote">on the compliance side too.</span>
        </>
      }
      lead="Chartered in 1984 as the first nationally chartered bankers' bank, FNBB has one business: supporting the growth of community banks, in more than 30 states. Clarid is built for the same banks — HMDA, fair lending, and CRA analysis and exam packaging, designed by a community-bank practitioner and validated by former FDIC and Federal Reserve examiners. This page sets out where the two can work together, how FNBB earns revenue from it, and how we handle the places where our services overlap."
      heroNote={
        <>
          Prepared for First National Bankers Bank, following our conversation with Jay Wisener.
          Roadmap items are marked as roadmap — nothing is described as shipped unless it is
          running in production today.
        </>
      }
      thesisKicker="§ 01 · WHY THIS PARTNERSHIP"
      thesisHeadline="Your banks need this work. You already hold the relationship."
      thesis={[
        {
          well: '01',
          title: 'The correspondent argument, applied to compliance',
          body: "A bankers' bank exists because some capabilities are beyond what a community bank can build alone. Compliance analytics is now one of them: fair lending regression, CRA assessment-area construction, and HMDA validation all take specialist depth that does not fit a two-person compliance department.",
        },
        {
          well: '02',
          title: 'A new line of revenue for FNBB',
          body: 'Every bank FNBB brings to Clarid is revenue FNBB shares in — on a recurring subscription, not a one-time fee. The analysis is ours to run; the relationship and the revenue stream stay with FNBB.',
        },
        {
          well: '03',
          title: 'It strengthens what FNBB already sells',
          body: "FNBB Services Corp. already provides compliance audits, a compliance subscription service, and loan review. Clarid can sit underneath that work — so FNBB's audits start from a validated, cited record instead of producing one by hand.",
        },
      ]}
      bringsHeadline="What Clarid brings to the banks you serve."
      depth={[
        {
          label: 'HMDA & LAR validation',
          txt: '170+ edit checks across validity, quality, macro-quality, and syntactical — each finding explained in plain language rather than an edit code. ML-assisted column mapping pulls the file straight out of the LOS, with geocoding and data-quality scoring on arrival.',
        },
        {
          label: 'Fair lending',
          txt: 'Underwriting, pricing, and redlining analysis with Census tract mapping and demographic overlays; denial-rate and pricing-disparity testing; logistic and OLS regression; and matched-pair file review. Consumer lending is covered too, from the CRA and trial-balance files, not just HMDA.',
        },
        {
          label: 'CRA lending & investment',
          txt: 'Assessment-area construction resolved to the correct OMB delineation for each analysis year; geographic and borrower distribution for HMDA, small business, small farm, and consumer loans; peer benchmarking from FDIC Call Report data; and bond-level validation of CRA investment credit for agency mortgage-backed securities.',
        },
        {
          label: 'Marketing compliance',
          txt: 'Review of marketing and advertising before it goes out, with program and policy management kept current against regulatory change.',
        },
        {
          label: 'Exam & board packaging',
          txt: 'Findings across marketing, HMDA, fair lending, and CRA assemble into a single exam-ready package and a board-ready summary — cross-referenced, timestamped, and hash-verified.',
        },
      ]}
      proofs={[
        {
          stat: 'One record, not five reports',
          desc: 'Marketing, HMDA, fair lending, redlining, regression, and CRA run as one connected record — a finding in one surfaces in the exam package without anyone reconciling spreadsheets by hand.',
        },
        {
          stat: 'Deterministic by construction',
          desc: 'AI performs detection; the compliance determination is a deterministic rules engine. The figure in the draft is the figure on the call three weeks later.',
        },
        {
          stat: 'Every figure resolves to a source',
          desc: "A tract income level traces to the Census record it came from; a qualification traces to the CFR section it rests on, under the bank's own primary regulator. Immutable audit trail and SHA-256-verified exports.",
        },
      ]}
      integration={{
        kicker: '§ 03 · WHERE WE CAN WORK TOGETHER',
        headline: 'The relationship is yours. The engine is ours.',
        lead: "FNBB already holds the trust relationship with its banks and already delivers work they cannot staff. Clarid supplies the compliance analytics underneath, so the bank experiences it as one more thing its bankers' bank does well.",
        barLabel: 'fnbb.relationship → clarid.engine → community bank, exam-ready',
        flow: [
          { name: "FNBB · bankers' bank", sub: 'Correspondent relationship · Services Corp. audits · Capital Markets' },
          { name: 'Clarid · compliance engine', sub: 'HMDA · fair lending · CRA · marketing · exam packaging', clarid: true },
          { name: 'Community bank', sub: 'Exam-ready, without adding compliance headcount' },
        ],
        tags: [
          { label: '✓ HMDA & LAR validation · shipped', kind: 'shipped' },
          { label: '✓ Fair lending & redlining · shipped', kind: 'shipped' },
          { label: '✓ CRA lending tables incl. consumer · shipped', kind: 'shipped' },
          { label: '✓ CRA investment-test bond validation · shipped', kind: 'shipped' },
          { label: '✓ Marketing compliance · shipped', kind: 'shipped' },
          { label: '✓ Exam & board packaging · shipped', kind: 'shipped' },
          { label: '◇ BSA/AML program governance · roadmap', kind: 'roadmap' },
          { label: '◇ Call Report preparation · roadmap', kind: 'roadmap' },
        ],
        note: "▢ Three places this fits FNBB's existing lines. Services Corp.: Clarid's output as the analytical base for FNBB's compliance audits and compliance subscription service. Capital Markets: independent validation of CRA investment credit on securities a bank holds — the bank is the client for that validation, not the seller, which is what keeps it independent. Correspondent relationship: banks FNBB introduces license Clarid directly, with FNBB sharing in the subscription. Call Report data is ingested today for peer benchmarking; Call Report preparation is roadmap.",
      }}
      sections={[
        {
          kickerLabel: 'REVENUE SHARING',
          headline: 'Recurring revenue for FNBB, on work FNBB does not have to staff.',
          lead: "Clarid is sold as a recurring subscription, so a bank FNBB brings in is revenue that continues for as long as the bank stays — not a one-time referral fee. Each shape below can earn FNBB revenue, and FNBB can run more than one at once.",
          cards: [
            {
              num: '01 · Referral',
              title: 'FNBB introduces, the bank subscribes',
              body: "The bank licenses Clarid directly and runs it itself. FNBB shares in the subscription revenue for as long as that bank remains a client — the simplest way to add a revenue line without adding staff.",
            },
            {
              num: '02 · Services',
              title: 'FNBB delivers on the platform',
              body: "FNBB Services Corp. runs compliance engagements with Clarid underneath — under the FNBB name, to FNBB's banks, at FNBB's fees. Clarid is the engine and stays out of sight.",
            },
            {
              num: '03 · Bundled',
              title: "Part of FNBB's compliance subscription",
              body: "Clarid's analysis folded into FNBB's existing compliance subscription service, so the banks already subscribing get more from it and FNBB has more to price it on.",
            },
          ],
          note: '▢ No percentages or terms are stated here because none have been agreed — the split, and which of these shapes FNBB wants, are ours to work out together. The referral and services models are running today with a channel partner, so the mechanics are not theoretical.',
        },
        {
          kickerLabel: 'WHERE OUR SERVICES OVERLAP',
          headline: "Where we overlap, FNBB's client stays FNBB's.",
          lead: "Some of what Clarid offers overlaps with services FNBB already provides. We would rather name those places than leave them unspoken. FNBB describes itself as a partner that never competes for its banks' business; Clarid will treat FNBB the same way.",
          cards: [
            {
              num: 'Overlap',
              title: 'Compliance audits and subscription',
              body: "FNBB Services Corp. provides compliance audits, custom audits, and a compliance subscription service. Clarid's HMDA, fair lending, and CRA analysis covers some of the same ground.",
            },
            {
              num: 'Overlap',
              title: 'Loan review and marketing review',
              body: "FNBB provides loan review services, and delivers social media compliance through its strategic alliances. Clarid reviews marketing for compliance, and has compliance-focused loan review on its roadmap.",
            },
            {
              num: 'Commitment',
              title: "Clarid won't go after FNBB's clients",
              body: "Clarid will not pursue FNBB's client banks for services FNBB provides, and will not take revenue from FNBB. Where a bank already relies on FNBB for a service, that relationship is FNBB's, and Clarid's role is to support it.",
            },
          ],
          note: '▢ The commitment would be written into the partnership agreement, alongside how introductions and shared clients are handled.',
        },
      ]}
      credibilityHeadline="Built by a community banker, validated by former regulators."
      credibility={[
        {
          num: 'FDIC',
          title: 'Jason Willard',
          body: "Partner in Clarid and a former FDIC Supervisory Examiner. Supervisory examination experience informs how Clarid frames findings, what a regulator will ask for next, and where a bank's record tends to fall apart under review.",
        },
        {
          num: 'Federal Reserve',
          title: 'Michael Wallace',
          body: 'Partner in Clarid, former Federal Reserve Supervisory Examiner and fair lending subject-matter expert, with roughly three decades of fair lending practice. He writes the specifications the fair lending, CRA, and HMDA engine is built to, and his test cases gate every change to it.',
        },
        {
          num: 'Evidence-grade',
          title: 'Everything is provable',
          body: 'Immutable, SHA-256 hash-chained audit trail and verified exports. Every check, finding, and override is recorded and attributable — which is what a regulator asks for.',
        },
      ]}
      contact={{ name: 'Bobby Sloan', title: 'Founder & CEO, Clarid', email: 'bobby@clarid.ai' }}
    />
  );
}
